import autoMarkAdressPlanned from './autoMarkAdressPlanned';
import createDefaultHouseVisitReminder, { defaultReminderNotice } from './createDefaultHouseVisitReminder';
import clearReplanLabel from './clearReplanLabel';
import { isAppointment } from './taskKind';

/**
 * Wat er na het bevestigen of inplannen van een huisbezoek hoort te gebeuren,
 * op één plek: de status van het huishouden automatisch op 'Ingepland' (als
 * de organisatie dat aan heeft), de automatische herinnering(en) per e-mail
 * en/of sms (idem), en het label 'Opnieuw inplannen' eraf (altijd — dat label
 * is een verzoek, en inplannen is het antwoord). Geeft terug wat de planner
 * moet lezen.
 *
 * Drie paden komen hier samen: een concept bevestigen op de huishoudenpagina,
 * een bestaand concept bevestigen in dezelfde lijst, en een huishouden in de
 * agenda slepen. De eerste twee liepen eerst elk hun eigen kopie, en de tweede
 * miste daardoor de helft (geen status, geen herinnering) — gevonden bij het
 * natesten van de sms-herinnering, 2026-10-01.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 * AI-CONTEXT: See docs/features/default-housevisit-reminders.md
 *
 * @param {object} params
 * @param {object} params.task - De bevestigde afspraak (ID, AdressID, Type)
 * @param {string} [params.authId] - Wie bevestigt
 * @param {boolean} params.autoPlan - `useSettings().autoPlanHouseVisitStatus`
 * @param {{enabled: boolean, channels: Array<'email'|'sms'>, daysBefore: number}} [params.reminderPlan] - `useSettings().defaultHouseVisitReminderPlan`
 * @returns {Promise<Array<string>>} Meldingen voor de planner, in volgorde; leeg als er niets te melden is
 */
const afterHouseVisitConfirmed = async ({ task, authId, autoPlan, reminderPlan }) => {
    // Alleen een afspraak zelf; een subtaak eronder draagt in de oude vorm
    // hetzelfde Type. AI-CONTEXT: See docs/pitfalls.md#database-subkind-hidden-behind-a-link-field
    if (!isAppointment(task)) return [];

    const notices = [];

    if (autoPlan) {
        const statusChanged = await autoMarkAdressPlanned(task, authId);
        if (statusChanged) notices.push("Status automatisch op 'Ingepland' gezet");
    }

    if (reminderPlan?.enabled) {
        const result = await createDefaultHouseVisitReminder({ task, authId, plan: reminderPlan });
        const notice = defaultReminderNotice(result);
        if (notice) notices.push(notice);
    }

    // Laatste stap, ná de taak-write: gooit deze, dan is het bezoek wél
    // bevestigd en mag het scherm niet 'niet gelukt' zeggen. Eigen catch met
    // een eerlijke melding; de fout zelf blijft zichtbaar in de console.
    // AI-CONTEXT: See docs/pitfalls.md#api-error-claims-nothing-was-saved
    try {
        if (await clearReplanLabel(task)) notices.push("Label 'Opnieuw inplannen' weggehaald");
    } catch (err) {
        console.error('clearReplanLabel mislukt na bevestigen van het huisbezoek', err);
        notices.push("Label 'Opnieuw inplannen' kon niet weggehaald worden");
    }

    return notices;
};

export default afterHouseVisitConfirmed;
